// @ts-nocheck - MUI 7.x Grid item prop type compatibility
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Autocomplete,
  TextField,
} from '@mui/material';
import {
  MergeType as MergeIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
  Close as CloseIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import {
  mfpModelMergeAPI,
  mfpModelsAPI,
  DuplicateModelPair,
  MFPModelMergeAnalysis,
  MFPModelSummary,
} from '../services/api';

export default function MFPModelMerge() {
  const [duplicates, setDuplicates] = useState<DuplicateModelPair[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPair, setSelectedPair] = useState<DuplicateModelPair | null>(null);
  const [analysis, setAnalysis] = useState<MFPModelMergeAnalysis | null>(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [merging, setMerging] = useState(false);
  const [minSimilarity, setMinSimilarity] = useState(0.8);
  const [mergeStrategy, setMergeStrategy] = useState('keep_primary');
  const [manufacturerFilter, setManufacturerFilter] = useState('');

  // Tab state
  const [activeTab, setActiveTab] = useState(0);

  // Manual merge state
  const [manualModel1, setManualModel1] = useState<any | null>(null);
  const [manualModel2, setManualModel2] = useState<any | null>(null);
  const [modelOptions, setModelOptions] = useState<any[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);

  const loadDuplicates = useCallback(async () => {
    try {
      setLoading(true);
      const data = await mfpModelMergeAPI.getDuplicates({
        min_similarity: minSimilarity,
        limit: 100,
        manufacturer_filter: manufacturerFilter || undefined,
      });
      setDuplicates(data);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load duplicate models');
    } finally {
      setLoading(false);
    }
  }, [minSimilarity, manufacturerFilter]);

  useEffect(() => {
    loadDuplicates();
  }, [loadDuplicates]);

  const searchModels = async (query: string) => {
    if (!query || query.length < 2) {
      setModelOptions([]);
      return;
    }

    try {
      setSearchLoading(true);
      const data = await mfpModelsAPI.autocomplete(query, 50);
      setModelOptions(data);
    } catch (err: any) {
      console.error('Failed to search models:', err);
      setModelOptions([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleManualAnalyze = async () => {
    if (!manualModel1 || !manualModel2) {
      setError('Please select both models to merge');
      return;
    }

    if (manualModel1.id === manualModel2.id) {
      setError('Cannot merge a model with itself');
      return;
    }

    setAnalyzing(true);

    try {
      const analysisData = await mfpModelMergeAPI.analyzeMerge(
        manualModel1.id,
        manualModel2.id
      );
      setAnalysis(analysisData);
      setSelectedPair({
        model1: manualModel1,
        model2: manualModel2,
        similarity: 1.0,
        match_type: 'manual_selection',
      } as any);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to analyze merge');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleAnalyze = async (pair: DuplicateModelPair) => {
    setSelectedPair(pair);
    setAnalyzing(true);

    try {
      const analysisData = await mfpModelMergeAPI.analyzeMerge(
        pair.model1.id,
        pair.model2.id
      );
      setAnalysis(analysisData);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to analyze merge');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleMerge = async () => {
    if (!selectedPair || !analysis) return;

    if (!window.confirm(
      `Are you sure you want to merge "${analysis.secondary_model.model_name}" into "${analysis.primary_model.model_name}"? This cannot be undone.`
    )) {
      return;
    }

    setMerging(true);

    try {
      await mfpModelMergeAPI.executeMerge({
        primary_model_id: analysis.primary_model.id,
        secondary_model_id: analysis.secondary_model.id,
        merge_strategy: mergeStrategy,
      });

      // Reload duplicates and close dialog
      await loadDuplicates();
      setSelectedPair(null);
      setAnalysis(null);
      alert('Models merged successfully!');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to merge models');
    } finally {
      setMerging(false);
    }
  };

  const handleIgnore = async (pair: DuplicateModelPair) => {
    if (!window.confirm(
      `Mark "${pair.model1.model_name}" and "${pair.model2.model_name}" as NOT duplicates? They will no longer appear in this list.`
    )) {
      return;
    }

    try {
      await mfpModelMergeAPI.ignorePair(
        pair.model1.id,
        pair.model2.id,
        'User marked as not a duplicate'
      );

      // Reload duplicates to remove the ignored pair
      await loadDuplicates();
      alert('Pair marked as not a duplicate and removed from the list.');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to ignore pair');
    }
  };

  const getMatchTypeColor = (matchType: string) => {
    switch (matchType) {
      case 'exact_name': return 'error';
      case 'normalized_name': return 'error';
      case 'similar_name': return 'warning';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box mb={3}>
        <Typography variant="h4" gutterBottom>
          Merge Duplicate MFP Models
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Find and merge duplicate MFP model records to consolidate equipment data
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ mb: 2 }}>
        <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
          <Tab label="Auto-Detected Duplicates" />
          <Tab label="Manual Merge" />
        </Tabs>
      </Paper>

      {activeTab === 0 && (
        <>
          <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Minimum Similarity</InputLabel>
              <Select
                value={minSimilarity}
                onChange={(e) => setMinSimilarity(Number(e.target.value))}
                label="Minimum Similarity"
              >
                <MenuItem value={0.6}>60% - Show more matches</MenuItem>
                <MenuItem value={0.8}>80% - Balanced</MenuItem>
                <MenuItem value={0.95}>95% - High confidence only</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Manufacturer</InputLabel>
              <Select
                value={manufacturerFilter}
                onChange={(e) => setManufacturerFilter(e.target.value)}
                label="Manufacturer"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="HP">HP</MenuItem>
                <MenuItem value="BROTHER">Brother</MenuItem>
                <MenuItem value="SHARP">Sharp</MenuItem>
                <MenuItem value="LEXMARK">Lexmark</MenuItem>
                <MenuItem value="EPSON">Epson</MenuItem>
                <MenuItem value="CANON">Canon</MenuItem>
                <MenuItem value="KOFAX">Kofax</MenuItem>
                <MenuItem value="SAMSUNG">Samsung</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Button
              fullWidth
              variant="outlined"
              onClick={loadDuplicates}
              disabled={loading}
            >
              Refresh
            </Button>
          </Grid>
          <Grid item xs={12} md={4}>
            <Chip
              label={`${duplicates.length} duplicate pairs found`}
              color="primary"
              icon={<InfoIcon />}
            />
          </Grid>
        </Grid>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Model 1</TableCell>
              <TableCell>Model 2</TableCell>
              <TableCell align="center">Similarity</TableCell>
              <TableCell align="center">Match Type</TableCell>
              <TableCell align="center">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {duplicates.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography variant="body2" color="text.secondary" py={3}>
                    No duplicate MFP models found at this similarity level
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              duplicates.map((pair, index) => (
                <TableRow key={index} hover>
                  <TableCell>
                    <ModelCell model={pair.model1} />
                  </TableCell>
                  <TableCell>
                    <ModelCell model={pair.model2} />
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={`${(pair.similarity * 100).toFixed(0)}%`}
                      size="small"
                      color={pair.similarity > 0.9 ? 'error' : 'warning'}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={pair.match_type.replace('_', ' ')}
                      size="small"
                      color={getMatchTypeColor(pair.match_type)}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<MergeIcon />}
                        onClick={() => handleAnalyze(pair)}
                      >
                        Analyze Merge
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        color="secondary"
                        startIcon={<CloseIcon />}
                        onClick={() => handleIgnore(pair)}
                      >
                        Not a Duplicate
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
        </>
      )}

      {activeTab === 1 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Manual Model Merge
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Search and select two models that you know are duplicates to merge them manually
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Autocomplete
                options={modelOptions}
                getOptionLabel={(option) => `${option.manufacturer} ${option.model_name} (${option.model_code})`}
                loading={searchLoading}
                value={manualModel1}
                onChange={(_, newValue) => setManualModel1(newValue)}
                onInputChange={(_, newInputValue) => {
                  searchModels(newInputValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Primary Model (keep this one)"
                    placeholder="Search by manufacturer, name, or code..."
                    helperText="This model will be kept and all data will merge into it"
                  />
                )}
                renderOption={(props, option) => {
                  const { key, ...otherProps } = props;
                  return (
                    <li key={key} {...otherProps}>
                      <Box>
                        <Typography variant="body2">
                          {option.manufacturer} {option.model_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Code: {option.model_code} | {option.equipment_count || 0} equipment
                        </Typography>
                      </Box>
                    </li>
                  );
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Autocomplete
                options={modelOptions}
                getOptionLabel={(option) => `${option.manufacturer} ${option.model_name} (${option.model_code})`}
                loading={searchLoading}
                value={manualModel2}
                onChange={(_, newValue) => setManualModel2(newValue)}
                onInputChange={(_, newInputValue) => {
                  searchModels(newInputValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Secondary Model (will be removed)"
                    placeholder="Search by manufacturer, name, or code..."
                    helperText="This model will be deleted after merging its data"
                  />
                )}
                renderOption={(props, option) => {
                  const { key, ...otherProps } = props;
                  return (
                    <li key={key} {...otherProps}>
                      <Box>
                        <Typography variant="body2">
                          {option.manufacturer} {option.model_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Code: {option.model_code} | {option.equipment_count || 0} equipment
                        </Typography>
                      </Box>
                    </li>
                  );
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Button
                variant="contained"
                size="large"
                fullWidth
                startIcon={<SearchIcon />}
                onClick={handleManualAnalyze}
                disabled={!manualModel1 || !manualModel2 || analyzing}
              >
                Analyze Merge
              </Button>
            </Grid>

            {manualModel1 && manualModel2 && (
              <Grid item xs={12}>
                <Alert severity="warning">
                  <Typography variant="body2">
                    <strong>You are about to merge:</strong>
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    • <strong>Keep:</strong> {manualModel1.manufacturer} {manualModel1.model_name} ({manualModel1.model_code})
                  </Typography>
                  <Typography variant="body2">
                    • <strong>Remove:</strong> {manualModel2.manufacturer} {manualModel2.model_name} ({manualModel2.model_code})
                  </Typography>
                </Alert>
              </Grid>
            )}
          </Grid>
        </Paper>
      )}

      {/* Merge Analysis Dialog */}
      <Dialog
        open={selectedPair !== null}
        onClose={() => {
          setSelectedPair(null);
          setAnalysis(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Merge Analysis
          {selectedPair && (
            <Typography variant="caption" display="block" color="text.secondary">
              Merging {selectedPair.model2.model_name} into {selectedPair.model1.model_name}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          {analyzing ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : analysis ? (
            <Box>
              {/* Data Migration Summary */}
              <Card sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Data to Migrate
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6} sm={4}>
                      <Typography variant="body2" color="text.secondary">
                        Equipment
                      </Typography>
                      <Typography variant="h5">
                        {analysis.data_to_migrate.equipment}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <Typography variant="body2" color="text.secondary">
                        Supply Links
                      </Typography>
                      <Typography variant="h5">
                        {analysis.data_to_migrate.model_supplies}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <Typography variant="body2" color="text.secondary">
                        Accessories
                      </Typography>
                      <Typography variant="h5">
                        {analysis.data_to_migrate.model_accessories}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <Typography variant="body2" color="text.secondary">
                        Sales Cases
                      </Typography>
                      <Typography variant="h5">
                        {analysis.data_to_migrate.sales_cases}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <Typography variant="body2" color="text.secondary">
                        Evaluations
                      </Typography>
                      <Typography variant="h5">
                        {analysis.data_to_migrate.evaluations}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Conflicts */}
              {analysis.conflicts.length > 0 && (
                <Card sx={{ mb: 2 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      <WarningIcon color="warning" sx={{ mr: 1, verticalAlign: 'middle' }} />
                      Conflicts ({analysis.conflicts.length})
                    </Typography>
                    <List dense>
                      {analysis.conflicts.map((conflict, idx) => (
                        <ListItem key={idx}>
                          <ListItemText
                            primary={conflict.type.replace('_', ' ').toUpperCase()}
                            secondary={`Primary: ${conflict.primary_value} | Secondary: ${conflict.secondary_value} → Keeping: ${conflict.kept_value}`}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              )}

              {/* Warnings */}
              {analysis.warnings.length > 0 && (
                <Card sx={{ mb: 2 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      <InfoIcon color="info" sx={{ mr: 1, verticalAlign: 'middle' }} />
                      Warnings ({analysis.warnings.length})
                    </Typography>
                    <List dense>
                      {analysis.warnings.map((warning, idx) => (
                        <ListItem key={idx}>
                          <ListItemText primary={warning} />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              )}

              {/* Merge Strategy */}
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Merge Strategy</InputLabel>
                <Select
                  value={mergeStrategy}
                  onChange={(e) => setMergeStrategy(e.target.value)}
                  label="Merge Strategy"
                >
                  <MenuItem value="keep_primary">Keep Primary Values</MenuItem>
                  <MenuItem value="keep_complete">Keep Most Complete Data</MenuItem>
                </Select>
              </FormControl>

              {analysis.is_safe ? (
                <Alert severity="success" icon={<CheckCircleIcon />}>
                  Merge is safe to proceed. No critical conflicts detected.
                </Alert>
              ) : (
                <Alert severity="warning" icon={<WarningIcon />}>
                  This merge has conflicts. Review carefully before proceeding.
                </Alert>
              )}
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setSelectedPair(null);
              setAnalysis(null);
            }}
            disabled={merging}
          >
            Cancel
          </Button>
          <Button
            onClick={handleMerge}
            variant="contained"
            color="primary"
            disabled={merging || !analysis}
            startIcon={merging ? <CircularProgress size={20} /> : <MergeIcon />}
          >
            {merging ? 'Merging...' : 'Merge Models'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function ModelCell({ model }: { model: MFPModelSummary }) {
  return (
    <Box>
      <Typography variant="body2" fontWeight="bold">
        {model.model_name}
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block">
        Code: {model.model_code} | Cost: ${model.equipment_cost}
      </Typography>
      <Box sx={{ mt: 0.5 }}>
        <Chip label={model.manufacturer} size="small" sx={{ mr: 0.5 }} />
        {model.is_color && (
          <Chip label="Color" size="small" color="primary" variant="outlined" sx={{ mr: 0.5 }} />
        )}
        <Chip
          label={`${model.equipment_count} equipment`}
          size="small"
          color="secondary"
          variant="outlined"
          sx={{ mr: 0.5 }}
        />
        <Chip
          label={`${model.model_supplies_count} supplies`}
          size="small"
          variant="outlined"
        />
      </Box>
    </Box>
  );
}
